/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Dashboard
 *| (C) Copyright IBM Corp. 2018, 2022
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
define([
	'../../../visualizations/recommender/VisRecommenderBindingFallback',
	'./Utils'
], function (VisRecommenderBindingFallback, Utils) {
	'use strict';
	return class VisRecommenderDelegator {
		constructor(options) {
			this.logger = options.logger;
			this.visDefinitions = options.visDefinitions;
			this.dataSources = options.dataSources;
		}

		destroy() {
			this.logger = null;
		}

		recommendBindingsForNewColumns(visualization, columnIds) {
			const bindingRecommender = new VisRecommenderBindingFallback(visualization);
			const recommendation = {
				slots:{},
				unbound: columnIds,
				visId: visualization.getDefinition().getId()
			};

			visualization.getSlots().getMappingInfoList().forEach(mapping => {
				let cols = recommendation.slots[mapping.slot.getId()];
				if (!cols){
					cols = [];
					recommendation.slots[mapping.slot.getId()] = cols;
				}
				cols.push(mapping.dataItem.getColumnId());
			});


			bindingRecommender.assignUnboundColumns(recommendation, this._getValidSlotList(visualization.getDefinition()));
			return Promise.resolve(recommendation);
		}

		_getValidSlotList(newDefinition) {
			return newDefinition.getSlotList().filter(slotDef=>!slotDef.isHidden());
		}

		recommendBindingsForSelectedViz(visualization, visId) {
			const newDefinition = this.visDefinitions.getById(visId);
			const bindingRecommender = new VisRecommenderBindingFallback(visualization);
			const recommendation = {
				slots:{},
				unbound: Utils.getUnboundColumnsIds(visualization),
				visId: newDefinition.getId()
			};
			bindingRecommender.assignUnboundColumns(recommendation, this._getValidSlotList(newDefinition));
			return Promise.resolve(recommendation);
		}

		recommendBestAlternateVisualization(visualization) {
			const mappings =  Utils.getUnboundColumnsIds(visualization);
			return this.recommendAlternateVisualizations(mappings, {
				dataSource: visualization.getDataSource()
			}).then(arr => arr[0]);
		}

		// Implement the basic/simple 'Vis Recommender' rules for CDE-CA-11.2 integration
		recommendAlternateVisualizations(columnIds, payload = {}) {
			let recommendation = {
				slots:{},
				visId: ''
			};

			let dataSource = payload.dataSource;
			if (!dataSource) {
				dataSource = this._getDataSource(payload.assetId);
			}
			if (dataSource) {
				switch(columnIds.length) {
					case 1:
						this._recommendAlternateVis_SingleColumnId(dataSource, columnIds, recommendation);
						break;
					case 2:
						this._recommendAlternateVis_DoubleColumnIds(dataSource, columnIds, recommendation);
						break;
					case 3:
						this._recommendAlternateVis_TripleColumnIds(dataSource, columnIds, recommendation);
						break;
				}
			}

			if (!recommendation.visId.length) {
				const visType = 'List'; // Default type for Table viz
				recommendation.visId = this.visDefinitions.getByType(visType).getId();
				recommendation.slots['grid_cols'] = columnIds;
			}
			return Promise.resolve([recommendation]);
		}

		_recommendAlternateVis_SingleColumnId(dataSource, columnIds, recommendation) {
			const metadataColumnList = columnIds.map( columnId => dataSource.getMetadataColumn(columnId) );
			const measureColumnIdList = this._getMeasureColumnIdList(metadataColumnList);
			if (measureColumnIdList.length === 1) { // One measure: Singleton as Summary viz
				recommendation.visId = this.visDefinitions.getByType('Singleton').getId();
				recommendation.slots['actual'] = measureColumnIdList;
			} else { // One location category: Tiledmap
				const locationColumnIdList = this._getLocationColumnIdList(metadataColumnList);
				if (locationColumnIdList.length === 1) {
					recommendation.visId = this.visDefinitions.getByType('Tiledmap').getId();
					recommendation.slots['locations'] = locationColumnIdList;
				}
			}
		}

		_recommendAlternateVis_DoubleColumnIds(dataSource, columnIds, recommendation) {
			const metadataColumnList = columnIds.map( columnId => dataSource.getMetadataColumn(columnId) );
			const measureColumnIdList = this._getMeasureColumnIdList(metadataColumnList);
			if (measureColumnIdList.length === 1) { // One measure and one category
				const locationColumnIdList = this._getLocationColumnIdList(metadataColumnList);
				if (locationColumnIdList.length === 1) { // One measure and one location category: Tiledmap
					recommendation.visId = this.visDefinitions.getByType('Tiledmap').getId();
					recommendation.slots['locations'] = [locationColumnIdList[0]];
					recommendation.slots['locationColor'] = [measureColumnIdList[0]];
				} else {
					const timeDimensionColumnIdList = this._getTimeDimensionColumnIdList(metadataColumnList);
					if (timeDimensionColumnIdList.length === 1) { // One measure and one time category: Line
						recommendation.visId = this.visDefinitions.getByType('Line').getId();
						recommendation.slots['categories'] = [timeDimensionColumnIdList[0]];
						recommendation.slots['values'] = [measureColumnIdList[0]];
					} else { // One measure and one non-location/non-time category: Column
						const categoryColumnIdList = this._getCategoryColumnIdList(metadataColumnList);
						recommendation.visId = this.visDefinitions.getByType('Column').getId();
						recommendation.slots['categories'] = [categoryColumnIdList[0]];
						recommendation.slots['values'] = [measureColumnIdList[0]];
					}
				}
			} else if (!measureColumnIdList.length) { // Two categories: PackedBubble
				recommendation.visId = this.visDefinitions.getByType('PackedBubble').getId();
				recommendation.slots['categories'] = [columnIds[0]];
				recommendation.slots['color'] = [columnIds[1]];
			}
		}

		_recommendAlternateVis_TripleColumnIds(dataSource, columnIds, recommendation) {
			const metadataColumnList = columnIds.map( columnId => dataSource.getMetadataColumn(columnId) );
			const measureColumnIdList = this._getMeasureColumnIdList(metadataColumnList);
			const locationColumnIdList = this._getLocationColumnIdList(metadataColumnList);
			if (measureColumnIdList.length && locationColumnIdList.length) {
				// One measure and two location categories OR Two measures and one location category: Tiledmap
				recommendation.visId = this.visDefinitions.getByType('Tiledmap').getId();
				if (locationColumnIdList.length === 2) {
					recommendation.slots['locations'] = [locationColumnIdList[0]];
					recommendation.slots['pointLocations'] = [locationColumnIdList[1]];
					recommendation.slots['locationColor'] = [measureColumnIdList[0]];
				} else {
					recommendation.slots['locations'] = [locationColumnIdList[0]];
					recommendation.slots['locationColor'] = [measureColumnIdList[0]];
					recommendation.slots['pointLocations'] = [locationColumnIdList[0]];
					recommendation.slots['pointSize'] = [measureColumnIdList[1]];
				}
			} else if (measureColumnIdList.length === 1) {
				const categoryColumnIdList = this._getCategoryColumnIdList(metadataColumnList);
				const timeDimensionColumnIdList = this._getTimeDimensionColumnIdList(metadataColumnList);
				if (timeDimensionColumnIdList.length === 1) { // One measure and one time category and one non-location/non-time category: Bar
					recommendation.visId = this.visDefinitions.getByType('Bar').getId();
					recommendation.slots['categories'] = [categoryColumnIdList[0]];
					recommendation.slots['values'] = [measureColumnIdList[0]];
					recommendation.slots['color'] = [timeDimensionColumnIdList[0]];
				} else if (timeDimensionColumnIdList.length === 2) { // One measure and two time categories: Heatmap
					recommendation.visId = this.visDefinitions.getByType('Heatmap').getId();
					recommendation.slots['categories'] = [timeDimensionColumnIdList[0]];
					recommendation.slots['series'] = [timeDimensionColumnIdList[1]];
					recommendation.slots['color'] = [measureColumnIdList[0]];
				} else { // One measure and two non-location/non-time categories: StackedBar
					recommendation.visId = this.visDefinitions.getByType('StackedBar').getId();
					recommendation.slots['categories'] = [categoryColumnIdList[0]];
					recommendation.slots['values'] = [measureColumnIdList[0]];
					recommendation.slots['color'] = [categoryColumnIdList[1]];
				}
			}
		}

		// Based on 1 visualization : 1 datasource mapping today
		_getDataSource(assetId) {
			let dataSource;
			if (assetId) {
				const dataSourceList = this.dataSources && this.dataSources.getDataSourceList();
				if (dataSourceList) {
					dataSource = dataSourceList.find( dataSource => dataSource.getAssetId() === assetId );
				}
			}
			return dataSource;
		}

		// Measure items
		_getMeasureColumnIdList(metadataColumnList) {
			const metaColumnList = metadataColumnList.filter( metaColumn => {
				return metaColumn.getType() === 'fact';
			});
			return metaColumnList.map( metaColumn => metaColumn.getId() );
		}

		// Categorical items except location or time items
		_getCategoryColumnIdList(metadataColumnList) {
			const metaColumnList = metadataColumnList.filter( metaColumn => {
				const taxonomyList = metaColumn.getTaxonomyList();
				return metaColumn.getType() === 'attribute'
						&& !metaColumn.isDateTimeDataType()
						&& (!taxonomyList || !this._isLocationTaxonomy(taxonomyList[0]) && !this._isTimeDimensionTaxonomy(taxonomyList[0]));
			});
			return metaColumnList.map( metaColumn => metaColumn.getId() );
		}

		// Location categorical items
		_getLocationColumnIdList(metadataColumnList) {
			const metaColumnList = metadataColumnList.filter( metaColumn => {
				const taxonomyList = metaColumn.getTaxonomyList();
				return metaColumn.getType() === 'attribute'
						&& !metaColumn.isDateTimeDataType()
						&& taxonomyList && this._isLocationTaxonomy(taxonomyList[0]);
			});
			return metaColumnList.map( metaColumn => metaColumn.getId() );
		}

		// Time categorical items
		_getTimeDimensionColumnIdList(metadataColumnList) {
			const metaColumnList = metadataColumnList.filter( metaColumn => {
				const taxonomyList = metaColumn.getTaxonomyList();
				return metaColumn.getType() === 'attribute'
						&& (metaColumn.isDateTimeDataType() || taxonomyList && this._isTimeDimensionTaxonomy(taxonomyList[0]));
			});
			return metaColumnList.map( metaColumn => metaColumn.getId() );
		}

		_isLocationTaxonomy(taxonomy) {
			return (taxonomy.getClass() === 'cGeoLocation');
		}

		_isTimeDimensionTaxonomy(taxonomy) {
			return (taxonomy.getClass() === 'cTime');
		}

		recommendRelatedVisualizations() {
			return Promise.resolve([]);
		}

		recommendCompareVisualizations() {
			return Promise.resolve([]);
		}

		recommendVisualizations() {
			return Promise.resolve([]);
		}
	};
});
